import { useEffect, useState } from 'react'
import { CircleDollarSign } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { billingApi, type BillingInvoice, type BillingMe } from '@/services/api/billing'
import { Button } from '@/components/common'
import { extractErrorMessage } from '@/utils/errorHandler'

const WANT_DEBIT_KEY = 'zahlung-want-debit'

const ROLE_LABEL: Record<string, string> = {
  superadmin: 'Superadmin',
  director: 'Direktor',
  hr: 'HR',
}

const STATUS_LABEL: Record<string, string> = {
  open: 'offen',
  paid: 'bezahlt',
  credited: 'gutgeschrieben',
}

function roleLabel(role: string) {
  return ROLE_LABEL[role] || role
}

function asId(v: string | null | undefined) {
  return v ? String(v) : ''
}

function parseDay(iso: string | Date | null | undefined): Date | null {
  if (!iso) return null
  if (iso instanceof Date) return Number.isNaN(iso.getTime()) ? null : iso
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

function formatDay(iso: string | Date | null | undefined) {
  const d = parseDay(iso)
  return d ? d.toLocaleDateString('de-CH') : '—'
}

function money(n: string | number, currency?: string | null) {
  const v = Number(n)
  const s = Number.isFinite(v)
    ? v.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : String(n)
  return currency ? `${s} ${currency}` : s
}

function periodLabel(month: number, year: number) {
  return format(new Date(year, month - 1, 1), 'LLLL yyyy', { locale: de })
}

function daysFromToday(iso: string | Date | null | undefined) {
  const d = parseDay(iso)
  if (!d) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  d.setHours(0, 0, 0, 0)
  return Math.round((d.getTime() - today.getTime()) / 86400000)
}

function hasMandate(status: string | undefined) {
  return Boolean(status) && status !== 'none'
}

function StepNum({ n, state }: { n: number; state: 'on' | 'done' | 'todo' }) {
  const cls =
    state === 'done'
      ? 'bg-emerald-600 text-white'
      : state === 'on'
        ? 'bg-indigo-600 text-white'
        : 'bg-gray-200 text-gray-600'
  return (
    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${cls}`}>
      {n}
    </div>
  )
}

export function SchoolBillingPage() {
  const [data, setData] = useState<BillingMe | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [payerId, setPayerId] = useState('')
  const [openNames, setOpenNames] = useState<string | null>(null)
  const [wantDebit, setWantDebit] = useState(() => sessionStorage.getItem(WANT_DEBIT_KEY) === '1')
  const [setupForced, setSetupForced] = useState<boolean | null>(null)

  const load = () => {
    billingApi.me().then((d) => {
      setData(d)
      setPayerId(asId(d.payer_user_id))
      setLoadError(null)
    }).catch((e) => setLoadError(extractErrorMessage(e, { loadError: 'Laden fehlgeschlagen' })))
  }

  useEffect(() => {
    const q = new URLSearchParams(window.location.search)
    const stripeReturn = q.get('stripe_mandate') === '1'
    const paypalReturn = !stripeReturn && (
      q.get('paypal_mandate') === '1' || q.get('mandate') === '1' || q.get('mandate') === 'paypal'
    )
    if (paypalReturn) {
      billingApi.paypalMandateComplete()
        .then(() => {
          window.history.replaceState({}, '', '/zahlung')
          load()
        })
        .catch((e) => {
          setActionError(extractErrorMessage(e, { loadError: 'PayPal-Freigabe nicht gespeichert' }))
          load()
        })
      return
    }
    if (stripeReturn || q.get('mandate')) {
      window.history.replaceState({}, '', '/zahlung')
    }
    load()
  }, [])

  useEffect(() => {
    if (!data) return
    if (sessionStorage.getItem(WANT_DEBIT_KEY) !== '1') return
    if (!hasMandate(data.mandate_status)) return
    if (data.collection_mode === 'debit') {
      sessionStorage.removeItem(WANT_DEBIT_KEY)
      setWantDebit(false)
      return
    }
    billingApi.setMode('debit')
      .then(() => {
        sessionStorage.removeItem(WANT_DEBIT_KEY)
        setWantDebit(false)
        load()
      })
      .catch(() => {
        sessionStorage.removeItem(WANT_DEBIT_KEY)
        setWantDebit(false)
      })
  }, [data])

  if (loadError && !data) return <div className="p-6 text-red-600">{loadError}</div>
  if (!data) return <div className="p-6">Laden…</div>

  const stripeOn = data.stripe === true
  const paypalOn = data.paypal === true
  const payerSet = Boolean(asId(data.payer_user_id))
  const mandateOk = hasMandate(data.mandate_status)
  const debitPath = data.collection_mode === 'debit' || wantDebit
  const step1Done = !data.is_superadmin || payerSet
  const setupIncomplete = !step1Done || (debitPath && !mandateOk)
  const setupOpen = setupIncomplete || setupForced === true
  const invoices = data.invoices || []
  const openInvoices = invoices.filter((inv) => inv.status === 'open')
  const openRemaining = openInvoices.reduce((sum, inv) => sum + Number(inv.remaining || 0), 0)
  const payTarget = [...openInvoices].sort((a, b) => {
    const da = parseDay(a.due_at)?.getTime() ?? Number.MAX_SAFE_INTEGER
    const db = parseDay(b.due_at)?.getTime() ?? Number.MAX_SAFE_INTEGER
    return da - db
  })[0]
  const nextDue = parseDay(payTarget?.due_at)
  const dueDays = nextDue ? daysFromToday(nextDue) : null
  const lockDays = daysFromToday(data.lock_warning)
  const currency = data.currency || ''
  const payer = (data.payer_candidates || []).find((c) => c.id === asId(data.payer_user_id))
  const payerName = payer ? payer.name : null
  const payerRole = payer ? roleLabel(payer.role) : null
  const isDebit = data.collection_mode === 'debit'

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    try {
      setActionError(null)
      await fn()
      load()
    } catch (e) {
      setActionError(extractErrorMessage(e, { loadError: fallback }))
    }
  }

  const startMandate = async (kind: 'stripe' | 'paypal') => {
    try {
      setActionError(null)
      const r = kind === 'stripe' ? await billingApi.stripeMandate() : await billingApi.paypalMandate()
      if (r.url) window.location.href = r.url
    } catch (e) {
      setActionError(extractErrorMessage(e, {
        loadError: kind === 'stripe' ? 'Stripe-Freigabe fehlgeschlagen' : 'PayPal-Freigabe fehlgeschlagen',
      }))
    }
  }

  const pay = async (inv: BillingInvoice, kind: 'stripe' | 'paypal') => {
    try {
      setActionError(null)
      const r = kind === 'stripe' ? await billingApi.stripeCheckout(inv.id) : await billingApi.paypalCheckout(inv.id)
      if (r.url) window.location.href = r.url
    } catch (e) {
      setActionError(extractErrorMessage(e, { loadError: 'Checkout fehlgeschlagen' }))
    }
  }

  const payFirst = () => {
    if (!payTarget) return
    if (stripeOn) return pay(payTarget, 'stripe')
    if (paypalOn) return pay(payTarget, 'paypal')
    return run(() => billingApi.downloadPdf(payTarget.id), 'PDF fehlgeschlagen')
  }

  const chooseRequest = () => {
    sessionStorage.removeItem(WANT_DEBIT_KEY)
    setWantDebit(false)
    if (data.collection_mode !== 'request') {
      void run(() => billingApi.setMode('request'), 'Modus fehlgeschlagen')
    }
  }

  const chooseDebit = () => {
    if (mandateOk) {
      sessionStorage.removeItem(WANT_DEBIT_KEY)
      setWantDebit(false)
      if (data.collection_mode !== 'debit') {
        void run(() => billingApi.setMode('debit'), 'Kein Mandat hinterlegt')
      }
      return
    }
    sessionStorage.setItem(WANT_DEBIT_KEY, '1')
    setWantDebit(true)
    setSetupForced(true)
  }

  const subtitleParts: string[] = []
  if (!step1Done) subtitleParts.push('Einrichtung unvollständig')
  else if (payerName) subtitleParts.push(`${payerName} ${isDebit ? 'zahlt automatisch' : 'zahlt selbst'}`)
  else subtitleParts.push(isDebit ? 'automatisch abbuchen' : 'zahlt selbst')
  if (currency) subtitleParts.push(currency)
  const subtitle = subtitleParts.join(' · ')

  const setupSummary = [
    payerName ? `${payerName}${payerRole ? ` (${payerRole})` : ''}` : payerSet ? 'Zahler gesetzt' : 'Zahler fehlt',
    isDebit ? 'automatisch abbuchen' : 'selbst zahlen',
    isDebit
      ? (data.mandate_status === 'paypal' ? 'PayPal' : data.mandate_status === 'both' ? 'Stripe und PayPal' : 'Stripe')
      : 'Freigabe nicht nötig',
  ].join(' · ')

  const step2Locked = Boolean(data.is_superadmin && !payerSet)
  const step1State: 'on' | 'done' | 'todo' = step1Done ? 'done' : 'on'
  const step2State: 'on' | 'done' | 'todo' = step2Locked ? 'todo' : (debitPath && !mandateOk ? 'on' : 'done')
  const step3State: 'on' | 'done' | 'todo' = mandateOk ? 'done' : 'on'

  return (
    <div className="flex flex-col min-h-full">
      <div className="px-4 lg:px-6 py-3 border-b bg-white flex items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
            <CircleDollarSign className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold">Zahlung</h1>
            <p className="text-xs text-gray-500 truncate">{subtitle}</p>
          </div>
        </div>
        {payTarget && Number(payTarget.remaining) > 0 && (
          <Button size="sm" onClick={() => void payFirst()}>
            {money(payTarget.remaining, currency)} jetzt zahlen
          </Button>
        )}
      </div>
      {actionError && <div className="bg-red-50 px-4 lg:px-6 py-3 text-sm text-red-700">{actionError}</div>}

      <div className="p-6 max-w-5xl space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <div className="text-xs text-gray-500">Offen</div>
            <div className="text-xl font-bold mt-0.5">{money(openRemaining)}</div>
            <div className="text-xs text-gray-500">
              {openInvoices.length === 1 ? 'eine Rechnung' : `${openInvoices.length} Rechnungen`}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <div className="text-xs text-gray-500">Fällig</div>
            <div className="text-lg font-bold mt-0.5">{nextDue ? formatDay(nextDue) : '—'}</div>
            <div className="text-xs text-gray-500">
              {dueDays == null ? 'keine offene Rechnung' : dueDays === 0 ? 'heute' : dueDays > 0 ? `in ${dueDays} Tagen` : `seit ${-dueDays} Tagen`}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <div className="text-xs text-gray-500">Sperre droht ab</div>
            <div className="text-lg font-bold mt-0.5">{data.lock_warning ? formatDay(data.lock_warning) : '—'}</div>
            <div className="text-xs text-gray-500">
              {lockDays == null ? 'kein zweiter Hinweis' : lockDays > 0 ? `in ${lockDays} Tagen` : lockDays === 0 ? 'heute' : 'überfällig'}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <div className="text-xs text-gray-500">Weg</div>
            <div className={`text-lg font-bold mt-0.5 ${!step1Done ? 'text-red-700' : ''}`}>
              {!step1Done ? '—' : isDebit ? 'Abbuchen' : 'Selbst'}
            </div>
            <div className="text-xs text-gray-500">
              {!step1Done ? 'Zahler fehlt' : isDebit ? 'am Fälligkeitstag' : 'keine Abbuchung'}
            </div>
          </div>
        </div>

        {!step1Done && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2.5 text-sm">
            Zahler ist nicht gesetzt. Superadmin kann trotzdem zahlen. Hinweise und Sperre laufen trotzdem — die Mails gehen an alle aktiven Superadmins.
          </div>
        )}
        {step1Done && data.lock_warning && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2.5 text-sm">
            Unbezahlt nach dem {formatDay(data.lock_warning)}: die ganze Schule wird gesperrt, bis der überfällige Saldo 0 ist. Teilzahlung reicht nicht.
          </div>
        )}

        {setupOpen ? (
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">Einrichtung</h2>
              {setupIncomplete ? (
                <span className="text-xs font-semibold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
                  {step1Done ? 'Schritt 2' : 'Schritt 1 von 2'}
                </span>
              ) : (
                <button type="button" className="text-xs text-gray-500 hover:text-gray-700" onClick={() => setSetupForced(false)}>
                  Zuklappen
                </button>
              )}
            </div>

            {data.is_superadmin && (
              <div className="flex gap-3">
                <StepNum n={1} state={step1State} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">Wer bezahlt?</div>
                  <p className="text-xs text-gray-500 mt-0.5 mb-2">
                    Eine Person: Superadmin, Direktor oder HR. Nur diese Person sieht «Zahlung» in ihrer Sidebar — Superadmin immer zusätzlich. Wechselt die Person, stirbt eine allfällige Abbuchungsfreigabe.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white"
                      value={payerId}
                      onChange={(e) => setPayerId(e.target.value)}
                    >
                      <option value="">— Person wählen —</option>
                      {(data.payer_candidates || []).map((c) => (
                        <option key={c.id} value={c.id}>{c.name} ({roleLabel(c.role)})</option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      disabled={!payerId}
                      onClick={() => payerId && run(() => billingApi.setPayer(payerId), 'Zahler speichern fehlgeschlagen')}
                    >
                      Speichern
                    </Button>
                    {payerSet && <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full font-medium">gesetzt</span>}
                  </div>
                </div>
              </div>
            )}

            <div className={`flex gap-3 ${step2Locked ? 'opacity-50 pointer-events-none' : ''}`}>
              <StepNum n={data.is_superadmin ? 2 : 1} state={step2State} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">Wie wird beglichen?</div>
                <p className="text-xs text-gray-500 mt-0.5 mb-2">
                  {step2Locked
                    ? 'Erst nach Schritt 1. Gilt ab der nächsten Rechnung.'
                    : 'Gilt ab der nächsten Rechnung. Offene Rechnungen bleiben beim bisherigen Weg. QR-Überweisung kann nicht abbuchen.'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    aria-label="Selbst zahlen"
                    className={`text-left border rounded-lg p-3 ${!debitPath ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white'}`}
                    onClick={chooseRequest}
                  >
                    <div className="text-sm font-semibold">Selbst zahlen</div>
                    <p className="text-xs text-gray-500 mt-1">Rechnung kommt per Mail. Zahler oder Superadmin zahlt per Karte, PayPal oder Swiss QR.</p>
                  </button>
                  <button
                    type="button"
                    aria-label="Automatisch abbuchen"
                    className={`text-left border rounded-lg p-3 ${debitPath ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white'}`}
                    onClick={chooseDebit}
                  >
                    <div className="text-sm font-semibold">Automatisch abbuchen</div>
                    <p className="text-xs text-gray-500 mt-1">Am Fälligkeitstag. Braucht eine einmalige Freigabe bei Stripe oder PayPal. QR bleibt der Notweg, wenn die Abbuchung scheitert.</p>
                  </button>
                </div>
              </div>
            </div>

            {debitPath && (
              <div className="flex gap-3">
                <StepNum n={data.is_superadmin ? 3 : 2} state={step3State} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">Abbuchungsfreigabe</div>
                  <p className="text-xs text-gray-500 mt-0.5 mb-2">
                    {mandateOk
                      ? 'Freigabe hinterlegt. Wechselt der Zahler, muss sie neu gesetzt werden.'
                      : 'Einmal bei Stripe oder PayPal bestätigen. Danach zieht die Plattform am Fälligkeitstag.'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {stripeOn && (
                      <Button size="sm" variant="ghost" onClick={() => startMandate('stripe')}>Freigabe bei Stripe</Button>
                    )}
                    {paypalOn && (
                      <Button size="sm" variant="ghost" onClick={() => startMandate('paypal')}>Freigabe bei PayPal</Button>
                    )}
                    {!stripeOn && !paypalOn && (
                      <p className="text-sm text-gray-500">Stripe und PayPal sind nicht konfiguriert. Zahlen bleibt über QR-PDF möglich.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            aria-label="Einrichtung"
            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-gray-50"
            onClick={() => setSetupForced(true)}
          >
            <span className="text-sm font-semibold">Einrichtung</span>
            <span className="text-xs text-gray-500 truncate">{setupSummary}</span>
          </button>
        )}

        <div>
          <h2 className="text-sm font-semibold mb-2">Rechnungen</h2>
          {!stripeOn && !paypalOn && (
            <p className="text-sm text-gray-500 mb-2">Stripe und PayPal sind nicht konfiguriert. Zahlen bleibt über QR-PDF möglich.</p>
          )}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b bg-gray-50">
                  <th className="px-3 py-2 font-medium text-xs uppercase tracking-wide text-gray-500">Rechnung</th>
                  <th className="px-3 py-2 font-medium text-xs uppercase tracking-wide text-gray-500">Monat</th>
                  <th className="px-3 py-2 font-medium text-xs uppercase tracking-wide text-gray-500">Offen / Brutto</th>
                  <th className="px-3 py-2 font-medium text-xs uppercase tracking-wide text-gray-500">Stand</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && (
                  <tr>
                    <td className="px-3 py-4 text-gray-500" colSpan={5}>Keine Rechnungen.</td>
                  </tr>
                )}
                {invoices.map((inv: BillingInvoice) => (
                  <tr key={inv.id} className="border-b last:border-b-0 align-top">
                    <td className="px-3 py-2.5">{inv.number}</td>
                    <td className="px-3 py-2.5">{periodLabel(inv.period_month, inv.period_year)}</td>
                    <td className="px-3 py-2.5">
                      <span className="font-semibold">{money(inv.remaining)}</span>
                      {' / '}
                      {money(inv.gross, inv.currency)}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                        inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700'
                          : inv.status === 'open' ? 'bg-amber-50 text-amber-800'
                            : 'bg-gray-100 text-gray-600'
                      }`}>
                        {STATUS_LABEL[inv.status] || inv.status}
                        {inv.has_ticket ? ' · gemeldet' : ''}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex gap-2 flex-wrap items-center">
                        <button
                          type="button"
                          className="text-indigo-700 text-xs font-medium hover:underline"
                          onClick={() => run(() => billingApi.downloadPdf(inv.id), 'PDF fehlgeschlagen')}
                        >
                          {inv.currency === 'CHF' ? 'PDF mit QR' : 'PDF'}
                        </button>
                        {inv.status === 'open' && stripeOn && (
                          <Button size="sm" onClick={() => pay(inv, 'stripe')}>Karte</Button>
                        )}
                        {inv.status === 'open' && paypalOn && (
                          <Button size="sm" variant="ghost" onClick={() => pay(inv, 'paypal')}>PayPal</Button>
                        )}
                        {!inv.has_ticket && (
                          <button
                            type="button"
                            className="text-indigo-700 text-xs font-medium hover:underline"
                            onClick={() => run(() => billingApi.dispute(inv.id), 'Meldung fehlgeschlagen')}
                          >
                            Betrag stimmt nicht
                          </button>
                        )}
                        {inv.lines?.length ? (
                          <button
                            type="button"
                            className="text-indigo-700 text-xs font-medium hover:underline"
                            onClick={() => setOpenNames(openNames === inv.id ? null : inv.id)}
                          >
                            {inv.user_count ? `${inv.user_count} User` : 'Userliste'}
                          </button>
                        ) : null}
                      </div>
                      {openNames === inv.id && (
                        <ul className="mt-2 text-xs text-gray-700">
                          {(inv.lines || []).map((ln, i) => (
                            <li key={i}>{ln.display_name} · {ln.fraction} · {ln.line_gross}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
